import { useEffect, useRef, useState } from 'react';

import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { IconTimesDefault } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

import { FormattedCurrency } from '../../../../../../iwIntlProvider/FormattedCurrency';
import { ContinueButton } from '../ContinueButton/ContinueButton';
import { RepaymentCalculatorButton } from '../RepaymentCalculatorButton/RepaymentCalculatorButton';
import {
    FeesRow,
    FullTermRow,
    InterestOnlyRepaymentRows,
    InterestRateRow,
    SummaryRow,
    TotalRepaymentRow
} from '../SummaryRow/SummaryRow';

export const MobileLoanSummary = ({
    selectedOffer,
    drawdownAmount,
    monthlyRepayment,
    isLoading,
    isSingleOffer
}: {
    selectedOffer: TFormalOffer | undefined;
    drawdownAmount: number | undefined;
    monthlyRepayment: number | null;
    isLoading: boolean;
    isSingleOffer: boolean;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const startY = useRef(0);
    const panelRef = useRef<HTMLDivElement>(null);

    const isInterestOnly = selectedOffer
        ? getIsInterestOnlyOffer(selectedOffer)
        : false;

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent | TouchEvent) => {
            if (
                panelRef.current &&
                !panelRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('pointerdown', handleClickOutside);
        return () =>
            document.removeEventListener('pointerdown', handleClickOutside);
    }, [isOpen]);

    return (
        <>
            <div
                ref={panelRef}
                className="fixed bottom-0 left-0 right-0 z-10 touch-none rounded-tl-l rounded-tr-l bg-primary-10 p-xl pt-m text-structure-100"
                onPointerDown={(e) => {
                    startY.current = e.clientY;
                }}
                onPointerUp={(e) => {
                    const delta = startY.current - e.clientY;
                    if (delta > 30) setIsOpen(true);
                    if (delta < -30) setIsOpen(false);
                }}
            >
                {isOpen && (
                    <>
                        <button
                            className="absolute right-m top-m"
                            onClick={() => setIsOpen(false)}
                            data-testid="close-mobile-summary"
                        >
                            <IconTimesDefault fontSize="24px" />
                        </button>

                        <Text className="py-m pr-xl text-xl font-bold text-structure-100">
                            {isSingleOffer && selectedOffer ? (
                                <FormattedMessage
                                    id="_.loan_summary.single_offer_title_mobile"
                                    defaultMessage="Your loan summary over {months} months"
                                    values={{
                                        months: getDurationInMonths(
                                            selectedOffer.duration!
                                        )
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="_.loan_summary.mobile_summary_heading"
                                    defaultMessage="Your loan summary"
                                />
                            )}
                        </Text>
                    </>
                )}

                {!isInterestOnly && (
                    <SummaryRow
                        label={
                            <FormattedMessage
                                id="_.loan_summary.monthly_repayment"
                                defaultMessage="Monthly repayment"
                            />
                        }
                        value={
                            <span className="text-xs font-bold text-structure-100">
                                <FormattedMessage
                                    id="_.loan_summary.min_monthly_repayment_mobile"
                                    defaultMessage="{amount} /month"
                                    values={{
                                        amount: (
                                            <span className="text-l">
                                                {monthlyRepayment !== null ? (
                                                    <FormattedCurrency
                                                        value={monthlyRepayment}
                                                    />
                                                ) : (
                                                    '£--.--'
                                                )}
                                            </span>
                                        )
                                    }}
                                />
                            </span>
                        }
                    />
                )}

                <div
                    className="overflow-hidden transition-[max-height] duration-300"
                    style={{ maxHeight: isOpen ? '500px' : '0px' }}
                    data-testid="summary-section"
                >
                    {isInterestOnly && (
                        <div className="flex flex-col gap-s pb-m">
                            <InterestOnlyRepaymentRows
                                selectedOffer={selectedOffer}
                                drawdownAmount={drawdownAmount}
                            />
                        </div>
                    )}
                    <div
                        className={`mt-m flex flex-col gap-s border-t border-secondary-20 pb-xl pt-m ${isLoading ? 'opacity-50' : ''}`}
                    >
                        <FullTermRow
                            selectedOffer={selectedOffer}
                            drawdownAmount={drawdownAmount}
                        />
                        <InterestRateRow
                            selectedOffer={selectedOffer}
                            drawdownAmount={drawdownAmount}
                        />
                        <FeesRow
                            selectedOffer={selectedOffer}
                            drawdownAmount={drawdownAmount}
                        />
                        <TotalRepaymentRow
                            selectedOffer={selectedOffer}
                            drawdownAmount={drawdownAmount}
                        />
                    </div>

                    <RepaymentCalculatorButton
                        selectedOffer={selectedOffer}
                        drawdownAmount={drawdownAmount}
                        variant="mobile"
                    />
                </div>

                <div className="pt-l">
                    {isOpen ? (
                        <ContinueButton
                            selectedOffer={selectedOffer}
                            drawdownAmount={drawdownAmount}
                        />
                    ) : (
                        <Button
                            className="w-full rounded-full text-primary-10"
                            variant="secondary-alt"
                            onClick={() => setIsOpen(true)}
                        >
                            <FormattedMessage
                                id="_.loan_summary.open_summary_button"
                                defaultMessage="Continue to summary"
                            />
                        </Button>
                    )}
                </div>
            </div>

            {/* Bottom spacer so the content behind the drawer isn't hidden */}
            <div className="h-[160px]" />
        </>
    );
};
